import { useCallback, useEffect, useRef, useState } from 'react';
import type { Track } from '@/types';
import Hls from 'hls.js';
import {
  CircleX,
  Fullscreen,
  Maximize,
  Minimize,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  SquareSquare,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { artworkUrl, mediaSessionArtwork } from '@/lib/api';
import { resolveTrackArtist } from '@/lib/artist';
import { SeekRange } from '@/components/ui/SeekRange';
import { setVideoSessionActive } from '@/lib/videoSession';

export type VideoPlaylistItem = {
  url: string;
  track: Track;
  /** Release cover art — Media Session + fullscreen meta (not the video poster). */
  artwork?: string;
  /** Video poster still for shelf / tray cards. */
  poster?: string;
  /** Release title for Media Session / fullscreen meta. */
  album?: string;
  /** Release artist fallback when the track has no artist. */
  artist?: string;
};

/** Named ladder rungs — labels always use these, even when pixel size differs. */
const QUALITY_RUNGS = [1080, 720, 540] as const;
type QualityRung = (typeof QUALITY_RUNGS)[number];

type LevelLike = {
  height: number;
  width: number;
  url?: string | string[];
};

function snapToRung(pixels: number): QualityRung {
  let best: QualityRung = QUALITY_RUNGS[0];
  let bestDist = Infinity;
  for (const rung of QUALITY_RUNGS) {
    const dist = Math.abs(pixels - rung);
    if (dist < bestDist) {
      best = rung;
      bestDist = dist;
    }
  }
  return best;
}

/** Resolve a level to 540 / 720 / 1080 via folder path, else nearest short-edge. */
function rungForLevel(level: LevelLike): QualityRung {
  const urls = Array.isArray(level.url) ? level.url : level.url ? [level.url] : [];
  for (const u of urls) {
    const m = u.match(/\/(540|720|1080)(?:\/|$)/);
    if (m) return Number(m[1]) as QualityRung;
  }
  const shortEdge = Math.min(level.width || Infinity, level.height || Infinity);
  if (Number.isFinite(shortEdge) && shortEdge > 0) return snapToRung(shortEdge);
  return snapToRung(level.height || level.width || 720);
}

function findLevelIndexForRung(levels: LevelLike[], rung: QualityRung): number {
  return levels.findIndex((l) => rungForLevel(l) === rung);
}

type Props = {
  videoUrl: string;
  track: Track;
  /** Release cover art for Media Session + fullscreen meta. */
  artwork?: string;
  album?: string;
  artist?: string;
  playlist?: VideoPlaylistItem[];
  onSelectVideo?: (item: VideoPlaylistItem) => void;
  onClose: () => void;
};

function publishVideoPosition(video: HTMLVideoElement, trackDuration?: number) {
  if (!('mediaSession' in navigator)) return;
  const fromEl = Number.isFinite(video.duration) ? video.duration : NaN;
  const duration =
    Number.isFinite(trackDuration) && (trackDuration as number) > 0
      ? (trackDuration as number)
      : Number.isFinite(fromEl) && fromEl > 0
        ? fromEl
        : NaN;
  if (!Number.isFinite(duration) || duration <= 0) return;
  const position = Math.min(Math.max(0, video.currentTime || 0), duration);
  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate: video.playbackRate || 1,
      position,
    });
  } catch {
    /* unsupported */
  }
}

export function VideoOverlay({
  videoUrl,
  track,
  artwork: artworkProp,
  album: albumProp,
  artist: artistProp,
  playlist = [],
  onSelectVideo,
  onClose,
}: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const onSelectVideoRef = useRef(onSelectVideo);
  onSelectVideoRef.current = onSelectVideo;
  const playlistRef = useRef(playlist);
  playlistRef.current = playlist;
  const videoUrlRef = useRef(videoUrl);
  videoUrlRef.current = videoUrl;

  // Always release cover — never the video poster frame.
  const sessionArt =
    artworkProp?.trim() ||
    playlist.find((p) => p.url === videoUrl)?.artwork?.trim() ||
    '';
  // Track artist → release artist → Zachary Gray (never album "Various Artists")
  const sessionArtist = resolveTrackArtist(
    track.artist,
    artistProp || playlist.find((p) => p.url === videoUrl)?.artist,
  );
  const sessionAlbum =
    albumProp?.trim() ||
    track.albumName?.trim() ||
    playlist.find((p) => p.url === videoUrl)?.album?.trim() ||
    '';

  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const seekScrubbingRef = useRef(false);
  const [qualityLabel, setQualityLabel] = useState('720p');
  const [levels, setLevels] = useState<Hls['levels']>([]);
  const [hideControls, setHideControls] = useState(false);
  const [letterboxed, setLetterboxed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [stageSize, setStageSize] = useState<{ width: number; height: number } | null>(null);
  const [animateStage, setAnimateStage] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stageAnimTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Live FS flag for ResizeObserver / fitStage — React state lags fullscreenchange. */
  const isFullscreenRef = useRef(false);

  const readFullscreenElement = () => {
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
    };
    return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
  };

  /** Apply FS chrome immediately so cover→contain doesn't wait on a React paint. */
  const syncFullscreenDom = (active: boolean) => {
    const overlay = overlayRef.current;
    const stage = stageRef.current;
    const video = videoRef.current;
    if (!overlay) return;
    overlay.classList.toggle('is-fullscreen', active);
    if (active) {
      overlay.classList.remove('letterboxed');
      // Full-bleed stage + contain: pillars/letterbox both paint from video/#000
      // (not the column --media-dark that showed as gray side bars).
      overlay.style.background = '#000';
      if (stage) {
        stage.style.width = '100%';
        stage.style.height = '100%';
        stage.style.background = '#000';
      }
      if (video) {
        video.style.objectFit = 'contain';
        video.style.background = '#000';
      }
      setStageSize(null);
    } else {
      overlay.style.background = '';
      if (stage) {
        stage.style.width = '';
        stage.style.height = '';
        stage.style.background = '';
      }
      if (video) {
        video.style.objectFit = '';
        video.style.background = '';
      }
    }
  };

  const IDLE_MS = 3000;

  const clearIdleTimer = () => {
    if (idleTimerRef.current != null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const bumpFullscreenControls = useCallback(() => {
    setHideControls(false);
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => setHideControls(true), IDLE_MS);
  }, []);

  const measureFull = () => {
    const overlay = overlayRef.current;
    if (!overlay) return null;
    return {
      width: Math.max(1, overlay.clientWidth),
      height: Math.max(1, overlay.clientHeight),
    };
  };

  const measureLetterbox = () => {
    const overlay = overlayRef.current;
    const video = videoRef.current;
    if (!overlay || !video?.videoWidth || !video.videoHeight) return null;
    const cw = overlay.clientWidth;
    const ch = overlay.clientHeight;
    const ar = video.videoWidth / video.videoHeight;
    let width = cw;
    let height = width / ar;
    if (height > ch) {
      height = ch;
      width = height * ar;
    }
    return {
      width: Math.max(1, Math.round(width)),
      height: Math.max(1, Math.round(height)),
    };
  };

  const fitStage = useCallback(() => {
    // Fullscreen: stage fills the FS viewport; object-fit:contain draws natural
    // pillars/letterbox in pure black (same paint for 4:3 sides and 16:9 bars).
    const fsEl = readFullscreenElement();
    const fs =
      isFullscreenRef.current ||
      (fsEl != null && fsEl === overlayRef.current);
    if (fs) {
      setStageSize(null);
      return;
    }
    const next = !letterboxed ? measureFull() : measureLetterbox();
    if (next) setStageSize(next);
  }, [letterboxed]);

  const toggleLetterbox = () => {
    if (isFullscreenRef.current || isFullscreen) return;
    const stage = stageRef.current;
    const overlay = overlayRef.current;
    // Pin current on-screen pixel size so CSS can interpolate (never % → px)
    if (stage && overlay) {
      setStageSize({
        width: stage.offsetWidth || overlay.clientWidth,
        height: stage.offsetHeight || overlay.clientHeight,
      });
    }
    setAnimateStage(true);
    if (stageAnimTimerRef.current) clearTimeout(stageAnimTimerRef.current);
    stageAnimTimerRef.current = setTimeout(() => setAnimateStage(false), 520);
    // Next frame: flip mode; fitStage effect then sets the target size
    requestAnimationFrame(() => {
      setLetterboxed((prev) => !prev);
    });
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setLevels([]);
    setQualityLabel('720p');

    const absolute = videoUrl.startsWith('http')
      ? videoUrl
      : new URL(videoUrl, 'https://media.thisiszacharygray.com').href;

    if (Hls.isSupported()) {
      const hls = new Hls({
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 5,
      });
      hlsRef.current = hls;
      hls.loadSource(absolute);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const available = hls.levels;
        setLevels(available);
        const idx720 = findLevelIndexForRung(available, 720);
        const fallback =
          idx720 >= 0
            ? idx720
            : available.reduce((best, level, i) => {
                const bestRung = available[best] ? rungForLevel(available[best]) : 0;
                return rungForLevel(level) > bestRung ? i : best;
              }, 0);
        if (available.length) {
          hls.nextLevel = fallback;
          setQualityLabel(`${rungForLevel(available[fallback])}p`);
        }
        void video.play();
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = absolute;
      void video.play();
    }

    setVideoSessionActive(true);

    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
      try {
        // Pause only — clearing src between playlist items flashes non-black.
        video.pause();
      } catch {
        /* ignore */
      }
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('previoustrack', null);
        navigator.mediaSession.setActionHandler('nexttrack', null);
        navigator.mediaSession.setActionHandler('seekto', null);
        navigator.mediaSession.setActionHandler('seekforward', null);
        navigator.mediaSession.setActionHandler('seekbackward', null);
      }
      // Keep session active across URL swaps; mount effect clears on unmount.
    };
  }, [videoUrl]);

  useEffect(() => {
    setVideoSessionActive(true);
    return () => setVideoSessionActive(false);
  }, []);

  // Keep Media Session metadata / actions on the *video* track — not the audio player.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    setVideoSessionActive(true);

    const list = playlistRef.current;
    const url = videoUrlRef.current;
    const idx = list.findIndex((p) => p.url === url);
    const canPrev = idx > 0;
    const canNext = idx >= 0 && idx < list.length - 1;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.name,
      artist: sessionArtist,
      album: sessionAlbum,
      artwork: mediaSessionArtwork(sessionArt),
    });

    navigator.mediaSession.setActionHandler('play', () => {
      void videoRef.current?.play();
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      videoRef.current?.pause();
    });

    // Playlist: next/prev track. Solo video: seek ± (same as before).
    const hasPlaylistNeighbors = canPrev || canNext;
    if (hasPlaylistNeighbors) {
      navigator.mediaSession.setActionHandler('seekforward', null);
      navigator.mediaSession.setActionHandler('seekbackward', null);
      navigator.mediaSession.setActionHandler(
        'previoustrack',
        canPrev
          ? () => {
              const i = playlistRef.current.findIndex(
                (p) => p.url === videoUrlRef.current,
              );
              const prev = i > 0 ? playlistRef.current[i - 1] : null;
              if (prev) onSelectVideoRef.current?.(prev);
            }
          : null,
      );
      navigator.mediaSession.setActionHandler(
        'nexttrack',
        canNext
          ? () => {
              const i = playlistRef.current.findIndex(
                (p) => p.url === videoUrlRef.current,
              );
              const next =
                i >= 0 && i < playlistRef.current.length - 1
                  ? playlistRef.current[i + 1]
                  : null;
              if (next) onSelectVideoRef.current?.(next);
            }
          : null,
      );
    } else {
      navigator.mediaSession.setActionHandler('previoustrack', null);
      navigator.mediaSession.setActionHandler('nexttrack', null);
      navigator.mediaSession.setActionHandler('seekbackward', (details) => {
        const v = videoRef.current;
        if (!v) return;
        v.currentTime = Math.max(0, v.currentTime - (details.seekOffset ?? 10));
        publishVideoPosition(v, track.duration);
      });
      navigator.mediaSession.setActionHandler('seekforward', (details) => {
        const v = videoRef.current;
        if (!v) return;
        const dur = Number.isFinite(v.duration) ? v.duration : track.duration || 0;
        v.currentTime = Math.min(dur, v.currentTime + (details.seekOffset ?? 10));
        publishVideoPosition(v, track.duration);
      });
    }

    navigator.mediaSession.setActionHandler('seekto', (details) => {
      const v = videoRef.current;
      if (!v || details.seekTime == null) return;
      v.currentTime = details.seekTime;
      publishVideoPosition(v, track.duration);
    });

    const v = videoRef.current;
    if (v) publishVideoPosition(v, track.duration);
  }, [
    videoUrl,
    track,
    sessionArt,
    sessionArtist,
    sessionAlbum,
    playlist,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setProgress(0);
    const onMeta = () => {
      fitStage();
      publishVideoPosition(video, track.duration);
    };
    const onTime = () => {
      if (seekScrubbingRef.current) return;
      if (video.duration) setProgress((video.currentTime / video.duration) * 100);
      publishVideoPosition(video, track.duration);
    };
    const onPlay = () => {
      setPlaying(true);
      if ('mediaSession' in navigator) {
        try {
          navigator.mediaSession.playbackState = 'playing';
        } catch {
          /* unsupported */
        }
      }
      publishVideoPosition(video, track.duration);
    };
    const onPause = () => {
      setPlaying(false);
      if ('mediaSession' in navigator) {
        try {
          navigator.mediaSession.playbackState = 'paused';
        } catch {
          /* unsupported */
        }
      }
      publishVideoPosition(video, track.duration);
    };
    const onEnded = () => {
      const idx = playlist.findIndex((p) => p.url === videoUrl);
      const next = idx >= 0 ? playlist[idx + 1] : null;
      if (next && onSelectVideo) onSelectVideo(next);
      else onClose();
    };
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('seeked', onTime);
    return () => {
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('seeked', onTime);
    };
  }, [onClose, fitStage, playlist, videoUrl, onSelectVideo, track.duration]);

  useEffect(() => {
    fitStage();
    const onResize = () => fitStage();
    window.addEventListener('resize', onResize);
    const ro =
      typeof ResizeObserver !== 'undefined' && overlayRef.current
        ? new ResizeObserver(() => fitStage())
        : null;
    if (overlayRef.current && ro) ro.observe(overlayRef.current);
    return () => {
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
    };
  }, [fitStage]);

  useEffect(() => {
    const onFs = () => {
      setAnimateStage(false);
      const fsEl = readFullscreenElement();
      const active = Boolean(fsEl && fsEl === overlayRef.current);
      isFullscreenRef.current = active;
      syncFullscreenDom(active);
      setIsFullscreen(active);
      // Re-measure after layout settles (enter: % stage; exit: column letterbox/cover).
      requestAnimationFrame(() => fitStage());
    };
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs as EventListener);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener(
        'webkitfullscreenchange',
        onFs as EventListener,
      );
    };
  }, [fitStage]);

  useEffect(() => {
    return () => {
      if (stageAnimTimerRef.current) clearTimeout(stageAnimTimerRef.current);
    };
  }, []);

  // Fullscreen: hide chrome + cursor after idle; wake on pointer activity
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!isFullscreen || !overlay) {
      clearIdleTimer();
      setHideControls(false);
      return;
    }

    bumpFullscreenControls();

    const onActivity = () => bumpFullscreenControls();
    overlay.addEventListener('mousemove', onActivity);
    overlay.addEventListener('mousedown', onActivity);
    overlay.addEventListener('touchstart', onActivity, { passive: true });
    overlay.addEventListener('keydown', onActivity);

    return () => {
      overlay.removeEventListener('mousemove', onActivity);
      overlay.removeEventListener('mousedown', onActivity);
      overlay.removeEventListener('touchstart', onActivity);
      overlay.removeEventListener('keydown', onActivity);
      clearIdleTimer();
    };
  }, [isFullscreen, bumpFullscreenControls]);

  const availableRungs = QUALITY_RUNGS.filter((rung) => findLevelIndexForRung(levels, rung) >= 0);
  const canCycleQuality = availableRungs.length >= 2;

  const cycleQuality = () => {
    const hls = hlsRef.current;
    if (!hls || !canCycleQuality) return;
    const levelIdx = hls.nextLevel >= 0 ? hls.nextLevel : hls.currentLevel;
    const current = levels[levelIdx] ? rungForLevel(levels[levelIdx]) : availableRungs[0];
    const pos = availableRungs.indexOf(current);
    const nextRung = availableRungs[(pos >= 0 ? pos + 1 : 0) % availableRungs.length];
    const idx = findLevelIndexForRung(levels, nextRung);
    if (idx < 0) return;
    hls.nextLevel = idx;
    setQualityLabel(`${nextRung}p`);
  };

  const iconProps = {
    size: 20,
    strokeWidth: 1.75,
    'aria-hidden': true as const,
  };
  const playlistIndex = playlist.findIndex((p) => p.url === videoUrl);
  const showVideoNav = playlist.length > 1 && playlistIndex >= 0;
  const canPrev = showVideoNav && playlistIndex > 0;
  const canNext = showVideoNav && playlistIndex < playlist.length - 1;

  const goVideo = (delta: number) => {
    if (!onSelectVideo || playlistIndex < 0) return;
    const next = playlist[playlistIndex + delta];
    if (next) onSelectVideo(next);
  };

  return (
    <div
      ref={overlayRef}
      className={[
        'video-overlay',
        hideControls ? 'hide-controls' : '',
        letterboxed && !isFullscreen ? 'letterboxed' : '',
        // Class-based FS styles — :fullscreen/:webkit-full-screen are unreliable
        // across browsers when the overlay (not <video>) is the FS root.
        isFullscreen ? 'is-fullscreen' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseEnter={() => {
        if (
          !isFullscreen &&
          window.matchMedia('(hover: hover) and (pointer: fine)').matches
        ) {
          setHideControls(false);
        }
      }}
      onMouseLeave={() => {
        if (
          !isFullscreen &&
          window.matchMedia('(hover: hover) and (pointer: fine)').matches
        ) {
          setHideControls(true);
        }
      }}
    >
      {!isFullscreen && (
        <button className="close-overlay" onClick={onClose} aria-label="Close video" type="button">
          <CircleX {...iconProps} />
        </button>
      )}

      {isFullscreen && (
        <div className="video-meta">
          {sessionArt ? (
            <img
              className="video-meta-art"
              src={artworkUrl(sessionArt)}
              alt=""
            />
          ) : null}
          <div className="video-meta-text">
            <div className="video-meta-title">{track.name}</div>
            <div className="video-meta-byline">
              <span className="video-meta-artist">{sessionArtist}</span>
              {sessionAlbum ? (
                <>
                  {' — '}
                  <span className="video-meta-release">{sessionAlbum}</span>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <div
        ref={stageRef}
        className={`video-stage${animateStage ? ' animate-size' : ''}`}
        style={
          stageSize
            ? { width: stageSize.width, height: stageSize.height }
            : { width: '100%', height: '100%' }
        }
      >
        <video
          ref={videoRef}
          id="track-video"
          autoPlay
          disablePictureInPicture
          controlsList="nodownload noremoteplayback noplaybackrate"
        />
      </div>

      <div className={`video-controls${letterboxed && !isFullscreen ? ' resized' : ''}`}>
        <div className="video-controls-leading">
          {showVideoNav ? (
            <button
              type="button"
              className="video-prev"
              aria-label="Previous video"
              disabled={!canPrev}
              onClick={() => goVideo(-1)}
            >
              <SkipBack {...iconProps} />
            </button>
          ) : null}

          <button
            type="button"
            className="play-pause-overlay"
            aria-label={playing ? 'Pause' : 'Play'}
            onClick={() => {
              const v = videoRef.current;
              if (!v) return;
              if (v.paused) void v.play();
              else v.pause();
            }}
          >
            {playing ? (
              <Pause {...iconProps} fill="currentColor" />
            ) : (
              <Play {...iconProps} fill="currentColor" />
            )}
          </button>

          {showVideoNav ? (
            <button
              type="button"
              className="video-next"
              aria-label="Next video"
              disabled={!canNext}
              onClick={() => goVideo(1)}
            >
              <SkipForward {...iconProps} />
            </button>
          ) : null}
        </div>

        <SeekRange
          className="timeline"
          value={progress}
          min={0}
          step={0.1}
          max={100}
          aria-label="Seek"
          liveSeek={false}
          onScrubbingChange={(scrubbing) => {
            seekScrubbingRef.current = scrubbing;
          }}
          onSeek={(next) => {
            setProgress(next);
            const v = videoRef.current;
            if (!v?.duration) return;
            v.currentTime = (next / 100) * v.duration;
          }}
        />

        <div className="video-controls-trailing">
          <button
            type="button"
            className="quality"
            onClick={cycleQuality}
            aria-label="Cycle quality"
            disabled={!canCycleQuality}
            title={
              canCycleQuality
                ? `Quality: ${availableRungs.map((h) => `${h}p`).join(' / ')}`
                : 'Only one quality available'
            }
          >
            <span key={qualityLabel} className="quality-value">
              {qualityLabel}
            </span>
          </button>

          <button
            type="button"
            className="mute-toggle"
            aria-label={muted ? 'Unmute' : 'Mute'}
            onClick={() => {
              const v = videoRef.current;
              if (!v) return;
              v.muted = !v.muted;
              setMuted(v.muted);
            }}
          >
            {muted ? <VolumeX {...iconProps} /> : <Volume2 {...iconProps} />}
          </button>

          <button
            type="button"
            className="resize"
            aria-label={letterboxed ? 'Expand to fill' : 'Fit to frame'}
            disabled={isFullscreen}
            title={
              isFullscreen
                ? 'Fit to frame is only available in the column view'
                : undefined
            }
            onClick={toggleLetterbox}
          >
            {letterboxed && !isFullscreen ? (
              <SquareSquare {...iconProps} />
            ) : (
              <Fullscreen {...iconProps} />
            )}
          </button>

          <button
            type="button"
            className="fullscreen"
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            onClick={() => {
              const root = overlayRef.current;
              if (!root) return;
              const doc = document as Document & {
                webkitFullscreenElement?: Element | null;
                webkitExitFullscreen?: () => void;
              };
              const fsEl = readFullscreenElement();
              if (fsEl) {
                if (doc.exitFullscreen) void doc.exitFullscreen();
                else doc.webkitExitFullscreen?.();
                return;
              }
              // Force natural AR before the FS transition paints (cover column → contain).
              isFullscreenRef.current = true;
              syncFullscreenDom(true);
              setAnimateStage(false);
              setIsFullscreen(true);
              const el = root as HTMLElement & {
                webkitRequestFullscreen?: () => void;
              };
              const req = el.requestFullscreen
                ? el.requestFullscreen()
                : el.webkitRequestFullscreen
                  ? Promise.resolve(el.webkitRequestFullscreen())
                  : null;
              if (req && typeof (req as Promise<void>).catch === 'function') {
                void (req as Promise<void>).catch(() => {
                  isFullscreenRef.current = false;
                  syncFullscreenDom(false);
                  setIsFullscreen(false);
                  fitStage();
                });
              }
            }}
          >
            {isFullscreen ? <Minimize {...iconProps} /> : <Maximize {...iconProps} />}
          </button>
        </div>
      </div>
    </div>
  );
}
